import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "styled-components";
import {
  FiAlertTriangle, FiChevronRight, FiFileText, FiGrid, FiInfo, FiLayers, FiList, FiRefreshCw, FiSearch, FiX,
} from "react-icons/fi";
import api from "../../services/api";
import {
  Empty, Group, IconButton, IconTile, LoadMore, Row, SearchField, Section, SectionHeader, Skeleton,
  Widget, WidgetGrid, tokens,
} from "../activity-logs/styles";
import { SegmentedControl } from "../activity-logs/components/primitives";
import { ActionButton, CreditsPage, Equation, Notice, SpanCell, Tag, TruncatedText } from "./styles";
import {
  categoryMeta, displayName, formatCNPJ, formatCount, formatCurrency, formatPercent, taxMeta,
} from "./dashboardData";
import { buildItemStats, formatDate, formatRate } from "./detailData";
import { withRetry } from "./useCreditosData";
import { DocumentBars } from "./components/charts";
import { BackButton, RankRows, ScrolledTopBar } from "./components/parts";
import ItemSheet from "./components/ItemSheet";

/**
 * Dashboard de um fornecedor dentro de um tipo de crédito: como o valor se compõe
 * e quais documentos o sustentam. Os números do topo vêm do resumo da API; os
 * cortes são calculados sobre os itens, paginados de 200 em 200 (teto da rota).
 * O conteúdo se adapta ao tipo: frete não tem produto nem NCM, mas tem rota.
 */
const PAGE_SIZE = 200;
const PAGES_PER_LOAD = 5;
const PAUSE_MS = 150;
const LIST_STEP = 60;

const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });
const shortKey = (nfe) => (nfe && nfe.length > 14 ? `…${nfe.slice(-10)}` : nfe || "Sem documento");

function ProfileRows({ title, rows, total }) {
  const theme = useTheme();
  if (rows.length === 0) return null;
  return (
    <div style={{ marginTop: 14 }}>
      <div style={{ fontSize: "0.76em", color: tokens.label2(theme), marginBottom: 6 }}>{title}</div>
      {rows.slice(0, 3).map((r) => (
        <div key={r.key} style={{ display: "flex", justifyContent: "space-between", gap: 10, fontSize: "0.84em", padding: "2px 0" }}>
          <span style={{ fontWeight: 500, minWidth: 0, overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{r.key}</span>
          <span style={{ color: tokens.label2(theme) }}>{total > 0 ? formatPercent((r.total / total) * 100) : formatCount(r.count)}</span>
        </div>
      ))}
    </div>
  );
}

function Overview({ resumo, stats, totalItens, complete, color, onOpenDocument, onGoDocuments }) {
  const theme = useTheme();
  const loadedCredit = stats.documents.reduce((sum, d) => sum + d.total, 0);
  const hasProducts = stats.products.length > 0;
  const hasNcm = stats.ncms.length > 0;

  // Frete não traz produto: a rota ocupa o lugar do ranking de produtos
  const primary = hasProducts
    ? { title: "Principais produtos", sub: "Crédito por descrição do item", rows: stats.products }
    : { title: "Principais rotas", sub: "Crédito por origem e destino", rows: stats.routes };
  const secondary = hasNcm
    ? { title: "Por NCM", sub: "Crédito por classificação fiscal", rows: stats.ncms }
    : { title: "Por CFOP", sub: "Crédito por natureza da operação", rows: stats.cfops };

  return (
    <>
      <Section $first>
        <WidgetGrid>
          <Widget $span={3} $delay={0}>
            <div className="w-head">Como o crédito se compõe</div>
            <Equation $color={color}>
              <div><span className="l">Base de cálculo</span><span className="v">{formatCurrency(resumo.totalValorBase)}</span></div>
              <span className="op">×</span>
              <div><span className="l">Alíquota média</span><span className="v">{formatRate(resumo.mediaAliquota)}</span></div>
              <span className="op">=</span>
              <div className="result"><span className="l">Crédito</span><span className="v">{formatCurrency(resumo.totalCredito)}</span></div>
            </Equation>
          </Widget>
          <Widget $delay={50} $clickable onClick={onGoDocuments}>
            <div className="w-head">Documentos</div>
            <div className="w-value">{formatCount(stats.documents.length)}{!complete && "+"}</div>
            <div className="w-foot">{formatCount(totalItens)} {totalItens === 1 ? "item" : "itens"}</div>
          </Widget>
        </WidgetGrid>
      </Section>

      <Section $delay={80}>
        <WidgetGrid>
          <Widget $span={3} $delay={100}>
            <div className="w-title">Crédito por mês de emissão</div>
            <div className="w-sub" style={{ marginBottom: 6 }}>
              {!complete && "Prévia: considera só os itens carregados"}
              {complete && stats.peak && <>Maior mês: <strong style={{ color: tokens.label(theme) }}>{stats.peak.label}</strong> · {formatCurrency(stats.peak.credito)}</>}
              {complete && !stats.peak && "Sem data de emissão nos itens"}
            </div>
            {stats.months.length > 0 && <DocumentBars data={stats.months} dataKey="credito" name="Crédito" color={color} />}
          </Widget>
          <Widget $delay={150}>
            <div className="w-title">Perfil</div>
            <div className="w-sub">{complete ? "Como os itens se distribuem" : "Prévia dos itens carregados"}</div>
            <ProfileRows title="Operação" rows={stats.operations} total={loadedCredit} />
            <ProfileRows title="Status" rows={stats.statuses} total={loadedCredit} />
            <ProfileRows title="Alíquota" rows={stats.rates} total={loadedCredit} />
          </Widget>
        </WidgetGrid>
      </Section>

      <Section $delay={140}>
        <WidgetGrid>
          <Widget $delay={200}>
            <div className="w-title">{primary.title}</div>
            <div className="w-sub" style={{ marginBottom: 14 }}>{primary.sub}</div>
            {primary.rows.length > 0 ? <RankRows rows={primary.rows} color={color} /> : <div className="w-sub">Sem dados</div>}
          </Widget>
          <Widget $delay={250}>
            <div className="w-title">{secondary.title}</div>
            <div className="w-sub" style={{ marginBottom: 14 }}>{secondary.sub}</div>
            {secondary.rows.length > 0 ? <RankRows rows={secondary.rows} color={color} /> : <div className="w-sub">Sem dados</div>}
          </Widget>
          <SpanCell $span={2}>
            <SectionHeader>
              <h2>{complete ? "Maiores documentos" : "Maiores documentos carregados"}</h2>
              <button className="link" onClick={onGoDocuments}>Ver todos <FiChevronRight /></button>
            </SectionHeader>
            <Group>
              {stats.documents.slice(0, 5).map((d) => (
                <Row key={d.key} $clickable $inset="60px" onClick={() => onOpenDocument(d)}>
                  <IconTile $size={32} $color={color}><FiFileText /></IconTile>
                  <div className="main">
                    <div className="title"><TruncatedText>{shortKey(d.nfe)}</TruncatedText></div>
                    <div className="subtitle">{formatDate(d.date)} · {d.items.length} {d.items.length === 1 ? "item" : "itens"}</div>
                  </div>
                  <div className="trailing">
                    <span className="value">{formatCurrency(d.total)}</span>
                    <FiChevronRight className="chevron" />
                  </div>
                </Row>
              ))}
            </Group>
          </SpanCell>
        </WidgetGrid>
      </Section>
    </>
  );
}

function SearchBar({ value, onChange, placeholder }) {
  return (
    <SearchField $max="420px">
      <FiSearch />
      <input placeholder={placeholder} aria-label={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
      {value && <button className="clear" aria-label="Limpar busca" onClick={() => onChange("")}><FiX /></button>}
    </SearchField>
  );
}

function NoResults({ term, label }) {
  return (
    <Group>
      <Empty>
        <div className="e-icon"><FiSearch /></div>
        <div className="e-title">{label}</div>
        {term && <div className="e-sub">Nada encontrado para &quot;{term}&quot;.</div>}
      </Empty>
    </Group>
  );
}

function DocumentList({ documents, color, onOpen }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("value");
  const [visible, setVisible] = useState(LIST_STEP);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = documents.filter((d) => !term || String(d.nfe).toLowerCase().includes(term));
    if (sort === "recent") return [...list].sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
    return list;
  }, [documents, q, sort]);

  return (
    <Section $first>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <SearchBar value={q} onChange={(v) => { setQ(v); setVisible(LIST_STEP); }} placeholder="Buscar pela chave do documento" />
        <div style={{ flex: 1 }} />
        <SegmentedControl small value={sort} onChange={setSort} options={[{ key: "value", label: "Maior valor" }, { key: "recent", label: "Recentes" }]} />
      </div>
      {filtered.length === 0 ? <NoResults term={q} label="Nenhum documento encontrado" /> : (
        <Group>
          {filtered.slice(0, visible).map((d) => (
            <Row key={d.key} $clickable $inset="60px" onClick={() => onOpen(d)}>
              <IconTile $size={32} $color={color}><FiFileText /></IconTile>
              <div className="main">
                <div className="title"><TruncatedText title={d.nfe}>{d.nfe || "Sem documento"}</TruncatedText></div>
                <div className="subtitle">{formatDate(d.date)} · {d.items.length} {d.items.length === 1 ? "item" : "itens"} · base {formatCurrency(d.base)}</div>
              </div>
              <div className="trailing">
                <span className="value">{formatCurrency(d.total)}</span>
                <FiChevronRight className="chevron" />
              </div>
            </Row>
          ))}
        </Group>
      )}
      {visible < filtered.length && (
        <LoadMore type="button" onClick={() => setVisible((v) => v + LIST_STEP)}>
          Mostrar mais ({formatCount(filtered.length - visible)} restantes)
        </LoadMore>
      )}
    </Section>
  );
}

function ItemList({ items, onOpen }) {
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("value");
  const [visible, setVisible] = useState(LIST_STEP);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    const list = items.filter((i) => !term
      || [i.descricao, i.nfe, i.cfop, i.ncm].some((v) => String(v || "").toLowerCase().includes(term)));
    if (sort === "recent") return [...list].sort((a, b) => String(b.dataEmissao || "").localeCompare(String(a.dataEmissao || "")));
    return [...list].sort((a, b) => (b.creditoRecuperavel || 0) - (a.creditoRecuperavel || 0));
  }, [items, q, sort]);

  return (
    <Section $first>
      <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 14 }}>
        <SearchBar value={q} onChange={(v) => { setQ(v); setVisible(LIST_STEP); }} placeholder="Buscar por descrição, documento, CFOP ou NCM" />
        <div style={{ flex: 1 }} />
        <SegmentedControl small value={sort} onChange={setSort} options={[{ key: "value", label: "Maior valor" }, { key: "recent", label: "Recentes" }]} />
      </div>
      {filtered.length === 0 ? <NoResults term={q} label="Nenhum item encontrado" /> : (
        <Group>
          {filtered.slice(0, visible).map((item, i) => (
            // eslint-disable-next-line react/no-array-index-key
            <Row key={item.id || i} $clickable onClick={() => onOpen(item)}>
              <div className="main">
                <div className="title"><TruncatedText title={item.descricao}>{item.descricao || (!item.nfe && item.ncm ? `Item sem descrição · NCM ${item.ncm}` : shortKey(item.nfe))}</TruncatedText></div>
                <div className="subtitle">
                  {[formatDate(item.dataEmissao), item.cfop && `CFOP ${item.cfop}`, item.ncm && `NCM ${item.ncm}`, `base ${formatCurrency(item.valorProduto)}`, formatRate(item.aliquotaAplicavel)].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div className="trailing">
                <span className="value" style={{ minWidth: 86, textAlign: "right" }}>{formatCurrency(item.creditoRecuperavel)}</span>
                <FiChevronRight className="chevron" />
              </div>
            </Row>
          ))}
        </Group>
      )}
      {visible < filtered.length && (
        <LoadMore type="button" onClick={() => setVisible((v) => v + LIST_STEP)}>
          Mostrar mais ({formatCount(filtered.length - visible)} restantes)
        </LoadMore>
      )}
    </Section>
  );
}

export default function FornecedorDetailScreen({ credito, fornecedor, backLabel = "Voltar", periodFiltered, onBack }) {
  const [state, setState] = useState({ items: [], resumo: {}, metodologia: {}, page: 0, totalPages: 1, totalItens: 0 });
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(false);
  const [tab, setTab] = useState("overview");
  const [sheet, setSheet] = useState(null);
  // Cada carga tem um id: se outra começar (Atualizar, remontagem), a anterior para
  // sem tocar no estado. Sem isso, duas cargas juntas duplicariam os itens.
  const runId = useRef(0);
  const cursor = useRef({ page: 0, totalPages: 1 });

  // Busca as próximas páginas em sequência, com pausa, respeitando o limite da API
  const loadPages = useCallback(async (count, id) => {
    const isStale = () => runId.current !== id;
    const projeto = localStorage.getItem("vidal_selected_project");
    for (let i = 0; i < count && cursor.current.page < cursor.current.totalPages; i += 1) {
      const nextPage = cursor.current.page + 1;
      // eslint-disable-next-line no-await-in-loop
      const { data } = await withRetry(() => api.get(`/creditos/modal/${credito.tipoCredito}`, {
        params: { projeto, page: nextPage, pageSize: PAGE_SIZE, fornecedor: fornecedor.codigo },
      }), isStale);
      if (isStale()) return;
      if (!data.success) throw new Error("Resposta sem sucesso");
      const d = data.data;
      cursor.current = { page: nextPage, totalPages: d.paginacao?.totalPages || 1 };
      setState((prev) => ({
        items: nextPage === 1 ? d.itensIdentificados || [] : [...prev.items, ...(d.itensIdentificados || [])],
        resumo: d.resumo || prev.resumo,
        metodologia: d.metodologia || prev.metodologia,
        page: nextPage,
        totalPages: d.paginacao?.totalPages || 1,
        totalItens: d.paginacao?.totalItens ?? d.resumo?.totalItens ?? 0,
      }));
      if (nextPage === 1) setLoading(false);
      // eslint-disable-next-line no-await-in-loop
      if (i < count - 1 && nextPage < cursor.current.totalPages) await sleep(PAUSE_MS);
    }
  }, [credito.tipoCredito, fornecedor.codigo]);

  const start = useCallback(async () => {
    runId.current += 1;
    const id = runId.current;
    cursor.current = { page: 0, totalPages: 1 };
    setLoading(true);
    setLoadingMore(true);
    setError(false);
    try {
      if (!fornecedor.codigo) throw new Error("Fornecedor sem código");
      await loadPages(PAGES_PER_LOAD, id);
    } catch (err) {
      if (runId.current !== id) return;
      console.error("Erro ao carregar os itens do fornecedor:", err);
      setError(true);
    } finally {
      if (runId.current === id) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  }, [fornecedor.codigo, loadPages]);

  useEffect(() => {
    start();
    return () => { runId.current += 1; };
  }, [start]);

  const loadMore = async () => {
    const id = runId.current;
    setLoadingMore(true);
    try {
      await loadPages(PAGES_PER_LOAD, id);
    } catch (err) {
      if (runId.current === id) setError(true);
    } finally {
      if (runId.current === id) setLoadingMore(false);
    }
  };

  const stats = useMemo(() => buildItemStats(state.items), [state.items]);
  const tax = taxMeta(credito.tipo);
  const loadedAll = state.page >= state.totalPages;
  // A paginação da API para em 10.000 itens; a quantidade do fornecedor é a contagem real
  const realTotal = Math.max(fornecedor.quantidade || 0, state.totalItens);
  const complete = loadedAll && state.items.length >= realTotal;
  const loadedCredit = stats.documents.reduce((sum, d) => sum + d.total, 0);
  const creditShare = state.resumo.totalCredito > 0 ? (loadedCredit / state.resumo.totalCredito) * 100 : 0;
  const name = fornecedor.nome || fornecedor.codigo;
  const closeSheet = useCallback(() => setSheet(null), []);

  let content;
  if (loading) {
    content = (
      <Section $first>
        <WidgetGrid>
          <Widget $span={3}><Skeleton $h={70} $r={10} /></Widget>
          <Widget><Skeleton $h={70} $r={10} /></Widget>
        </WidgetGrid>
        <div style={{ height: 14 }} />
        <Skeleton $h={280} $r={16} />
      </Section>
    );
  } else if (error && state.items.length === 0) {
    content = (
      <Section $first>
        <Group>
          <Empty>
            <div className="e-icon"><FiAlertTriangle /></div>
            <div className="e-title">Não foi possível carregar este fornecedor</div>
            <div className="e-sub" style={{ marginBottom: 14 }}>Verifique a conexão e tente de novo.</div>
            <ActionButton onClick={start} style={{ margin: "0 auto" }}><FiRefreshCw /> Tentar novamente</ActionButton>
          </Empty>
        </Group>
      </Section>
    );
  } else if (tab === "documents") {
    content = <DocumentList documents={stats.documents} color={tax.color} onOpen={(document) => setSheet({ document })} />;
  } else if (tab === "items") {
    content = <ItemList items={state.items} onOpen={(item) => setSheet({ item })} />;
  } else {
    content = (
      <Overview
        resumo={state.resumo}
        stats={stats}
        totalItens={realTotal}
        complete={complete}
        color={tax.color}
        onOpenDocument={(document) => setSheet({ document })}
        onGoDocuments={() => setTab("documents")}
      />
    );
  }

  return (
    <>
      <CreditsPage>
        <ScrolledTopBar scrollToTop={true}>
          <div className="title-row">
            <div style={{ minWidth: 0 }}>
              <BackButton onClick={onBack}>{backLabel}</BackButton>
              <h1 title={name}>{displayName(name)}</h1>
              <div className="meta" style={{ flexWrap: "wrap" }}>
                <Tag $color={tax.color} style={{ fontSize: "1em" }}>{tax.label}</Tag>
                <span className="update-tag">{categoryMeta(credito.categoria).label}</span>
                {fornecedor.cnpj && <span className="update-tag">{formatCNPJ(fornecedor.cnpj)}</span>}
                {fornecedor.uf && <span className="update-tag">{fornecedor.uf}</span>}
              </div>
            </div>
            <div className="actions">
              <IconButton onClick={start} title="Atualizar" aria-label="Atualizar">
                <FiRefreshCw className={loading || loadingMore ? "spin" : ""} />
              </IconButton>
            </div>
          </div>
          <div className="nav-row">
            <SegmentedControl
              value={tab}
              onChange={setTab}
              options={[
                { key: "overview", icon: <FiGrid />, label: "Visão geral" },
                { key: "documents", icon: <FiLayers />, label: "Documentos", count: loading ? undefined : stats.documents.length },
                { key: "items", icon: <FiList />, label: "Itens", count: loading ? undefined : state.items.length },
              ]}
            />
          </div>
        </ScrolledTopBar>

        {periodFiltered && (
          <Notice style={{ marginTop: 0, marginBottom: 12 }}>
            <FiInfo />
            <span>Esta tela mostra todo o período: a API não aplica o filtro de período aqui.</span>
          </Notice>
        )}

        {!loading && !error && !complete && (
          <Notice style={{ marginTop: 0, marginBottom: 12 }}>
            <FiInfo />
            <span style={{ flex: 1 }}>
              Base, alíquota e crédito são os totais da API. Gráficos e listas são uma prévia: {formatCount(state.items.length)} de {formatCount(realTotal)} itens, com {formatPercent(creditShare)} do crédito.
              {loadedAll && " A API não entrega mais itens deste fornecedor."}
            </span>
            {!loadedAll && (
              <ActionButton onClick={loadMore} disabled={loadingMore} style={{ height: 28 }}>
                {loadingMore ? "Carregando..." : "Carregar mais"}
              </ActionButton>
            )}
          </Notice>
        )}

        {content}
      </CreditsPage>

      {sheet && (
        <ItemSheet
          item={sheet.item}
          document={sheet.document}
          metodologia={state.metodologia}
          color={tax.color}
          onClose={closeSheet}
        />
      )}
    </>
  );
}
